import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, UploadCloud, FileText, CheckCircle2, Copy } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES } from "@/constants/options";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FILING_FIELD_LABELS = {
  company_name: "Company / Shipper Name",
  email: "Email",
  shipment_direction: "Shipment Direction",
  country: "Country",
};

function validateFilingForm(form, file) {
  const required = ["company_name", "email", "shipment_direction", "country"];
  for (const field of required) {
    if (!String(form[field] || "").trim()) {
      return `${FILING_FIELD_LABELS[field]} is required.`;
    }
  }
  if (!EMAIL_RE.test(form.email.trim())) {
    return "Email: please enter a valid email address.";
  }
  if (!file) {
    return "Please attach a commercial invoice or packing list.";
  }
  return null;
}

export const CustomsFiling = () => {
  const [form, setForm] = useState({ company_name: "", email: "", phone: "", shipment_direction: "", country: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState(null);
  const fileRef = useRef(null);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    const handlePrefill = (e) => {
      const detail = e.detail || {};
      setForm((f) => ({ ...f, ...detail }));
      toast.success("Filing form pre-filled from your chat with the Compliance Assistant");
    };
    window.addEventListener("fergusons:prefill-filing", handlePrefill);
    return () => window.removeEventListener("fergusons:prefill-filing", handlePrefill);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateFilingForm(form, file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("company_name", form.company_name);
      fd.append("email", form.email);
      if (form.phone.trim()) fd.append("phone", form.phone);
      fd.append("shipment_direction", form.shipment_direction);
      fd.append("country", form.country);
      fd.append("file", file);
      const { data } = await apiClient.post("/customs-filings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Filing request received. We'll begin processing right away.");
      setReferenceNumber(data.reference_number);
      setForm({ company_name: "", email: "", phone: "", shipment_direction: "", country: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyReference = () => {
    navigator.clipboard.writeText(referenceNumber);
    toast.success("Reference number copied");
  };

  return (
    <section id="customs-filing" className="py-20 lg:py-28 bg-cream-alt" data-testid="customs-filing-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-10">
        <Reveal>
          <p className="label-eyebrow text-gold-muted mb-3">Customs Filing</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-ink">
            Start Your Customs Filing Today
          </h2>
          <p className="mt-3 text-base text-ink-soft max-w-xl">
            Upload your commercial invoice or packing list and we'll begin pre-arrival clearance.
          </p>
        </Reveal>

        {referenceNumber && (
          <Reveal>
            <div
              data-testid="filing-reference-confirmation"
              className="mt-8 rounded-xl border border-gold/30 bg-gold/10 p-5 flex items-center justify-between gap-4 flex-wrap"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-gold-muted shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-ink">
                    Filing received! Save your reference number to track this filing:
                  </p>
                  <p className="font-mono font-semibold text-lg text-ink mt-1" data-testid="filing-reference-number">
                    {referenceNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={copyReference}
                data-testid="filing-reference-copy-btn"
                className="flex items-center gap-1.5 text-sm font-medium text-gold-muted hover:text-gold border border-gold/30 hover:border-gold/60 rounded-full px-4 py-2 transition-colors"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            noValidate
            data-testid="customs-filing-form"
            className="mt-10 rounded-2xl border border-gold/20 bg-white shadow-sm p-6 sm:p-10 text-ink"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="filing_company_name">Company / Shipper Name</Label>
                <Input
                  id="filing_company_name"
                  data-testid="filing-input-company-name"
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  className="mt-1.5 bg-cream-alt border-ink/15 text-ink"
                />
              </div>
              <div>
                <Label>Shipment Direction</Label>
                <Select value={form.shipment_direction} onValueChange={(v) => set("shipment_direction", v)}>
                  <SelectTrigger data-testid="filing-select-direction" className="mt-1.5 bg-cream-alt border-ink/15">
                    <SelectValue placeholder="Import or Export" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Import">Import</SelectItem>
                    <SelectItem value="Export">Export</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filing_email">Email</Label>
                <Input
                  id="filing_email"
                  type="email"
                  data-testid="filing-input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="mt-1.5 bg-cream-alt border-ink/15 text-ink"
                />
              </div>
              <div>
                <Label htmlFor="filing_phone">Phone (optional)</Label>
                <Input
                  id="filing_phone"
                  data-testid="filing-input-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="mt-1.5 bg-cream-alt border-ink/15 text-ink"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Country of Origin or Destination</Label>
                <Select value={form.country} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="filing-select-country" className="mt-1.5 bg-cream-alt border-ink/15">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5">
              <Label>Commercial Invoice or Packing List</Label>
              <label
                htmlFor="filing_file"
                data-testid="filing-upload-dropzone"
                className="mt-1.5 flex items-center gap-3 border border-dashed border-ink/25 hover:border-gold/60 rounded-xl p-5 cursor-pointer transition-colors bg-cream-alt"
              >
                {file ? <FileText size={20} className="text-gold-muted shrink-0" /> : <UploadCloud size={20} className="text-gold-muted shrink-0" />}
                <span className="text-sm text-ink-soft truncate">
                  {file ? file.name : "Click to upload PDF, JPG, or PNG"}
                </span>
              </label>
              <input
                ref={fileRef}
                id="filing_file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                data-testid="filing-file-input"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              data-testid="filing-submit-btn"
              className="mt-7 w-full sm:w-auto bg-transparent border border-gold text-gold-muted hover:bg-gold/10 disabled:opacity-60 font-display font-semibold text-sm sm:text-base px-8 py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Start a Customs Filing
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
